// frontend/src/components/crm/SendingProfileBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sendingProfilesAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SendingProfileBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState({
        name: '',
        description: '',
        domain: '',
        provider: 'custom',
        providerConfig: {
            apiKey: '',
            apiSecret: '',
            region: '',
            endpoint: ''
        },
        fromName: '',
        fromEmail: '',
        replyTo: '',
        isDefault: false,
        dkim: {
            enabled: false,
            selector: '',
            privateKey: '',
            publicKey: ''
        },
        spf: {
            enabled: false,
            record: ''
        },
        dmarc: {
            enabled: false,
            policy: 'none',
            record: ''
        },
        bimi: {
            enabled: false,
            logoUrl: '',
            record: ''
        },
        throttling: {
            maxPerDay: 1000,
            maxPerHour: 100
        },
        warmup: {
            enabled: false,
            plan: 'moderate',
            targetVolume: 1000
        },
        settings: {
            trackOpens: true,
            trackClicks: true,
            requireTls: true,
            enableBounceTracking: true
        }
    });

    useEffect(() => {
        if (isEditing) {
            loadProfile();
        }
    }, [id, isEditing]);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await sendingProfilesAPI.getById(id);
            if (response.data.success) {
                const loadedProfile = response.data.data;
                // Ensure provider has a default value if missing (for old profiles)
                if (!loadedProfile.provider) {
                    loadedProfile.provider = 'custom';
                }
                setProfile(loadedProfile);
            } else {
                toast.error('Failed to load sending profile');
                navigate('/sending-profiles');
            }
        } catch (error) {
            console.error('Failed to load sending profile:', error);
            toast.error('Failed to load sending profile');
            navigate('/sending-profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validate required fields
        if (!profile.name || !profile.fromName || !profile.fromEmail) {
            toast.error('Please fill in all required fields (Name, From Name, From Email)');
            return;
        }
        
        // Domain is only required for custom provider
        // Handle old profiles that might not have provider field (default to 'custom')
        const provider = profile.provider || 'custom';
        if (provider === 'custom' && !profile.domain) {
            toast.error('Domain is required when using a custom domain. Please enter a domain or select an email service provider.');
            return;
        }

        try {
            setSaving(true);
            let response;
            if (isEditing) {
                response = await sendingProfilesAPI.update(id, profile);
            } else {
                response = await sendingProfilesAPI.create(profile);
            }

            if (response.data.success) {
                toast.success(`Sending profile ${isEditing ? 'updated' : 'created'} successfully`);
                navigate('/sending-profiles');
            }
        } catch (error) {
            console.error('Failed to save sending profile:', error);
            toast.error('Failed to save sending profile');
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async () => {
        if (!id) {
            toast.error('Please save the profile first before verifying');
            return;
        }

        try {
            const response = await sendingProfilesAPI.verify(id, {});
            if (response.data.success) {
                toast.success('Domain verification started');
                loadProfile();
            }
        } catch (error) {
            console.error('Failed to verify sending profile:', error);
            toast.error('Failed to verify sending profile');
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="text-center py-12">Loading sending profile...</div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/sending-profiles')}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {isEditing ? 'Edit Sending Profile' : 'Create Sending Profile'}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {isEditing ? 'Update your sending profile settings' : 'Configure email sending domain and authentication'}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate('/sending-profiles')}
                        className="px-4 py-2 border border-input rounded-lg hover:bg-muted"
                    >
                        Cancel
                    </button>
                    {isEditing && (
                        <button
                            onClick={handleVerify}
                            className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10"
                        >
                            Verify Domain
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Profile'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic Information */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Name <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={profile.name}
                                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="e.g., Main Domain"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={profile.description || ''}
                                    onChange={(e) => setProfile(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    rows={3}
                                    placeholder="Optional description"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={profile.isDefault || false}
                                        onChange={(e) => setProfile(prev => ({ ...prev, isDefault: e.target.checked }))}
                                        className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                    />
                                    <span className="text-sm text-foreground">Set as default sending profile</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Domain Configuration */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Domain Configuration</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Email Provider
                                </label>
                                <select
                                    value={profile.provider || 'custom'}
                                    onChange={(e) => setProfile(prev => ({ ...prev, provider: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                >
                                    <option value="custom">Custom Domain (I have my own domain)</option>
                                    <option value="sendgrid">SendGrid</option>
                                    <option value="mailgun">Mailgun</option>
                                    <option value="ses">AWS SES</option>
                                    <option value="postmark">Postmark</option>
                                    <option value="mailchimp">Mailchimp Transactional</option>
                                    <option value="other">Other Email Service Provider</option>
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {profile.provider === 'custom' 
                                        ? 'Use your own domain for sending emails'
                                        : 'Use an email service provider (no domain required)'
                                    }
                                </p>
                            </div>
                            
                            {profile.provider === 'custom' && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Domain
                                    </label>
                                    <input
                                        type="text"
                                        value={profile.domain || ''}
                                        onChange={(e) => setProfile(prev => ({ ...prev, domain: e.target.value }))}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                        placeholder="example.com"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        The domain you'll be sending emails from. Don't have a domain? 
                                        <a href="https://www.namecheap.com/domains/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                                            Get one here
                                        </a> or use an email service provider above.
                                    </p>
                                </div>
                            )}
                            
                            {profile.provider !== 'custom' && (
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                    <p className="text-sm text-blue-800 dark:text-blue-200">
                                        <strong>Using {profile.provider === 'sendgrid' ? 'SendGrid' : 
                                                      profile.provider === 'mailgun' ? 'Mailgun' :
                                                      profile.provider === 'ses' ? 'AWS SES' :
                                                      profile.provider === 'postmark' ? 'Postmark' :
                                                      profile.provider === 'mailchimp' ? 'Mailchimp' : 'Email Service Provider'}</strong>
                                        <br />
                                        No domain required. Configure your provider credentials below. The provider will handle domain verification automatically.
                                    </p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    From Name <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={profile.fromName}
                                    onChange={(e) => setProfile(prev => ({ ...prev, fromName: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="Your Company Name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    From Email <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={profile.fromEmail}
                                    onChange={(e) => setProfile(prev => ({ ...prev, fromEmail: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="noreply@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Reply-To Email
                                </label>
                                <input
                                    type="email"
                                    value={profile.replyTo || ''}
                                    onChange={(e) => setProfile(prev => ({ ...prev, replyTo: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="support@example.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Authentication */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Email Authentication</h2>
                        <div className="space-y-6">
                            {/* DKIM */}
                            <div className="border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={profile.dkim?.enabled || false}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                dkim: { ...prev.dkim, enabled: e.target.checked }
                                            }))}
                                            className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                        />
                                        <label className="text-sm font-medium text-foreground">DKIM</label>
                                    </div>
                                    {isEditing && profile.verification?.dkim?.status && (
                                        <span className={`text-xs px-2 py-1 rounded ${
                                            profile.verification.dkim.status === 'verified' ? 'bg-green-100 text-green-800' :
                                            profile.verification.dkim.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                            {profile.verification.dkim.status}
                                        </span>
                                    )}
                                </div>
                                {profile.dkim?.enabled && (
                                    <div className="space-y-3 mt-3">
                                        <div>
                                            <label className="block text-xs text-muted-foreground mb-1">Selector</label>
                                            <input
                                                type="text"
                                                value={profile.dkim?.selector || ''}
                                                onChange={(e) => setProfile(prev => ({
                                                    ...prev,
                                                    dkim: { ...prev.dkim, selector: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
                                                placeholder="default"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-muted-foreground mb-1">Private Key</label>
                                            <textarea
                                                value={profile.dkim?.privateKey || ''}
                                                onChange={(e) => setProfile(prev => ({
                                                    ...prev,
                                                    dkim: { ...prev.dkim, privateKey: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm font-mono"
                                                rows={4}
                                                placeholder="-----BEGIN PRIVATE KEY-----"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-muted-foreground mb-1">Public Key</label>
                                            <textarea
                                                value={profile.dkim?.publicKey || ''}
                                                onChange={(e) => setProfile(prev => ({
                                                    ...prev,
                                                    dkim: { ...prev.dkim, publicKey: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm font-mono"
                                                rows={3}
                                                placeholder="-----BEGIN PUBLIC KEY-----"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* SPF */}
                            <div className="border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={profile.spf?.enabled || false}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                spf: { ...prev.spf, enabled: e.target.checked }
                                            }))}
                                            className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                        />
                                        <label className="text-sm font-medium text-foreground">SPF</label>
                                    </div>
                                    {isEditing && profile.verification?.spf?.status && (
                                        <span className={`text-xs px-2 py-1 rounded ${
                                            profile.verification.spf.status === 'verified' ? 'bg-green-100 text-green-800' :
                                            profile.verification.spf.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                            {profile.verification.spf.status}
                                        </span>
                                    )}
                                </div>
                                {profile.spf?.enabled && (
                                    <div className="mt-3">
                                        <label className="block text-xs text-muted-foreground mb-1">SPF Record</label>
                                        <input
                                            type="text"
                                            value={profile.spf?.record || ''}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                spf: { ...prev.spf, record: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm font-mono"
                                            placeholder="v=spf1 include:_spf.example.com ~all"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* DMARC */}
                            <div className="border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={profile.dmarc?.enabled || false}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                dmarc: { ...prev.dmarc, enabled: e.target.checked }
                                            }))}
                                            className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                        />
                                        <label className="text-sm font-medium text-foreground">DMARC</label>
                                    </div>
                                    {isEditing && profile.verification?.dmarc?.status && (
                                        <span className={`text-xs px-2 py-1 rounded ${
                                            profile.verification.dmarc.status === 'verified' ? 'bg-green-100 text-green-800' :
                                            profile.verification.dmarc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                            {profile.verification.dmarc.status}
                                        </span>
                                    )}
                                </div>
                                {profile.dmarc?.enabled && (
                                    <div className="space-y-3 mt-3">
                                        <div>
                                            <label className="block text-xs text-muted-foreground mb-1">Policy</label>
                                            <select
                                                value={profile.dmarc?.policy || 'none'}
                                                onChange={(e) => setProfile(prev => ({
                                                    ...prev,
                                                    dmarc: { ...prev.dmarc, policy: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
                                            >
                                                <option value="none">None</option>
                                                <option value="quarantine">Quarantine</option>
                                                <option value="reject">Reject</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-muted-foreground mb-1">DMARC Record</label>
                                            <input
                                                type="text"
                                                value={profile.dmarc?.record || ''}
                                                onChange={(e) => setProfile(prev => ({
                                                    ...prev,
                                                    dmarc: { ...prev.dmarc, record: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm font-mono"
                                                placeholder="v=DMARC1; p=none; rua=mailto:dmarc@example.com"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Throttling & Warm-up */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Throttling & Warm-up</h2>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Max Emails Per Day
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.throttling?.maxPerDay || 1000}
                                        onChange={(e) => setProfile(prev => ({
                                            ...prev,
                                            throttling: { ...prev.throttling, maxPerDay: parseInt(e.target.value) || 1000 }
                                        }))}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Max Emails Per Hour
                                    </label>
                                    <input
                                        type="number"
                                        value={profile.throttling?.maxPerHour || 100}
                                        onChange={(e) => setProfile(prev => ({
                                            ...prev,
                                            throttling: { ...prev.throttling, maxPerHour: parseInt(e.target.value) || 100 }
                                        }))}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={profile.warmup?.enabled || false}
                                        onChange={(e) => setProfile(prev => ({
                                            ...prev,
                                            warmup: { ...prev.warmup, enabled: e.target.checked }
                                        }))}
                                        className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                    />
                                    <span className="text-sm text-foreground">Enable warm-up plan</span>
                                </label>
                            </div>
                            {profile.warmup?.enabled && (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">Warm-up Plan</label>
                                        <select
                                            value={profile.warmup?.plan || 'moderate'}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                warmup: { ...prev.warmup, plan: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                        >
                                            <option value="conservative">Conservative</option>
                                            <option value="moderate">Moderate</option>
                                            <option value="aggressive">Aggressive</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">Target Volume</label>
                                        <input
                                            type="number"
                                            value={profile.warmup?.targetVolume || 1000}
                                            onChange={(e) => setProfile(prev => ({
                                                ...prev,
                                                warmup: { ...prev.warmup, targetVolume: parseInt(e.target.value) || 1000 }
                                            }))}
                                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Settings</h3>
                        <div className="space-y-3">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={profile.settings?.trackOpens || false}
                                    onChange={(e) => setProfile(prev => ({
                                        ...prev,
                                        settings: { ...prev.settings, trackOpens: e.target.checked }
                                    }))}
                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                />
                                <span className="text-sm text-foreground">Track Opens</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={profile.settings?.trackClicks || false}
                                    onChange={(e) => setProfile(prev => ({
                                        ...prev,
                                        settings: { ...prev.settings, trackClicks: e.target.checked }
                                    }))}
                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                />
                                <span className="text-sm text-foreground">Track Clicks</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={profile.settings?.requireTls || false}
                                    onChange={(e) => setProfile(prev => ({
                                        ...prev,
                                        settings: { ...prev.settings, requireTls: e.target.checked }
                                    }))}
                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                />
                                <span className="text-sm text-foreground">Require TLS</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={profile.settings?.enableBounceTracking || false}
                                    onChange={(e) => setProfile(prev => ({
                                        ...prev,
                                        settings: { ...prev.settings, enableBounceTracking: e.target.checked }
                                    }))}
                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                />
                                <span className="text-sm text-foreground">Enable Bounce Tracking</span>
                            </label>
                        </div>
                    </div>

                    {isEditing && profile.verification && (
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Verification Status</h3>
                            <div className="space-y-3">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-muted-foreground">Domain</span>
                                        {profile.verification.domain?.status === 'verified' ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-red-600" />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-muted-foreground">DKIM</span>
                                        {profile.verification.dkim?.status === 'verified' ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-red-600" />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-muted-foreground">SPF</span>
                                        {profile.verification.spf?.status === 'verified' ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-red-600" />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-muted-foreground">DMARC</span>
                                        {profile.verification.dmarc?.status === 'verified' ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-red-600" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SendingProfileBuilder;

